// §11.13 — the Console's pure half: merge, filter, format. No React, no kernel, no DOM.
import type { ConsoleEntry, ConsoleLevel, ConsoleSource } from '@hangar/shared';

export const LEVELS: readonly ConsoleLevel[] = ['debug', 'info', 'warn', 'error'];

/** Rank, so "warn and above" is a comparison rather than a set membership. */
const RANK: Readonly<Record<ConsoleLevel, number>> = { debug: 0, info: 1, warn: 2, error: 3 };

export interface ConsoleFilter {
  /**
   * Source ids to **hide**. Empty hides nothing.
   *
   * An exclude-list, not an include-list, and that is not a style choice. With a list of
   * sources to *show*, empty has to mean "all" — nobody wants a Console that starts blank — and
   * then "the user turned every source off" is also an empty list, so the two opposite states
   * are indistinguishable. Worse, from the all-shown state a click on a lit chip would add its
   * source and read as *solo this one*, which is the reverse of what a lit chip promises.
   * Excluding has one representation per state and no such corner.
   */
  hiddenSources: readonly string[];
  /** The lowest level shown. */
  minLevel: ConsoleLevel;
  /** Case-insensitive substring over message, detail, source and app id. */
  text: string;
}

export const EMPTY_FILTER: ConsoleFilter = { hiddenSources: [], minLevel: 'debug', text: '' };

/**
 * Every source's entries in one time order.
 *
 * Sorted by `at` and then by `seq`: two entries can land in the same millisecond — a bus event
 * that logs two lines does exactly that — and `seq` is the only thing that still knows which
 * came first. Without the tiebreak the list would shuffle those two rows on every re-render,
 * because `sort` is stable over the *input* order and the input is being rebuilt each time.
 *
 * `seq` is per-source, so it breaks ties only within a source. Across sources a same-millisecond
 * tie is genuinely unordered, and the source id keeps that arbitrary answer at least *stable*.
 */
export function mergeEntries(sources: readonly ConsoleSource[]): ConsoleEntry[] {
  const all: Array<{ entry: ConsoleEntry; source: string }> = [];
  for (const source of sources) {
    for (const entry of source.entries()) all.push({ entry, source: source.id });
  }
  all.sort((a, b) => {
    if (a.entry.at !== b.entry.at) return a.entry.at - b.entry.at;
    if (a.source !== b.source) return a.source < b.source ? -1 : 1;
    return a.entry.seq - b.entry.seq;
  });
  return all.map((row) => row.entry);
}

export function matches(entry: ConsoleEntry, filter: ConsoleFilter): boolean {
  if (filter.hiddenSources.includes(entry.source)) return false;
  if (RANK[entry.level] < RANK[filter.minLevel]) return false;
  const needle = filter.text.trim().toLowerCase();
  if (needle === '') return true;
  return (
    entry.message.toLowerCase().includes(needle) ||
    (entry.detail ?? '').toLowerCase().includes(needle) ||
    entry.source.toLowerCase().includes(needle) ||
    (entry.appId ?? '').toLowerCase().includes(needle)
  );
}

export function applyFilter(
  entries: readonly ConsoleEntry[],
  filter: ConsoleFilter,
): ConsoleEntry[] {
  return entries.filter((entry) => matches(entry, filter));
}

/** How many of each level are in `entries` — the counts the toolbar shows. */
export function countByLevel(
  entries: readonly ConsoleEntry[],
): Readonly<Record<ConsoleLevel, number>> {
  const counts: Record<ConsoleLevel, number> = { debug: 0, info: 0, warn: 0, error: 0 };
  for (const entry of entries) counts[entry.level] += 1;
  return counts;
}

/** `14:03:22.481` — local, to the millisecond, because ordering questions are sub-second. */
export function formatTime(at: number): string {
  const date = new Date(at);
  const pad = (value: number, width = 2): string => String(value).padStart(width, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(
    date.getMilliseconds(),
    3,
  )}`;
}

/** One entry as a line of text, for copy and for export. */
export function formatEntry(entry: ConsoleEntry): string {
  const head = `${formatTime(entry.at)}  ${entry.level.toUpperCase().padEnd(5)} ${entry.source.padEnd(7)}`;
  const app = entry.appId ? ` [${entry.appId}]` : '';
  const detail = entry.detail ? `  — ${entry.detail}` : '';
  return `${head}${app} ${entry.message}${detail}`;
}

export function formatEntries(entries: readonly ConsoleEntry[]): string {
  return entries.map(formatEntry).join('\n');
}
